import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

interface ApiErrorResponse {
  message: string;
  statusCode: number;
  error?: string;
  details?: any;
}

/**
 * Получить initData текущей платформы в виде строки.
 * Возвращает объект { prefix, data } или null если не в Telegram/MAX.
 */
function getPlatformInitData(): { prefix: 'tma' | 'max'; data: string } | null {
  if (typeof window === 'undefined') return null;

  // Telegram
  const telegramInitData = window.Telegram?.WebApp?.initData;
  if (telegramInitData) {
    return { prefix: 'tma', data: telegramInitData };
  }

  // MAX (window.WebApp is the correct object according to MAX docs)
  if (window.WebApp?.initData) {
    try {
      // MAX provides initData as an encoded string
      const dataStr = window.WebApp.initData;
      if (!dataStr) return null;

      return { prefix: 'max', data: dataStr };
    } catch (e) {
      console.error('[ApiClient] Failed to get MAX initData:', e);
      return null;
    }
  }

  return null;
}

/**
 * Получить clinicId из доступных источников
 */
function getClinicId(): string | null {
  if (typeof window === 'undefined') return null;

  // 1. Telegram startParam (передаётся при открытии мини-приложения из бота)
  const telegramStartParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
  if (telegramStartParam) return telegramStartParam;

  // 2. MAX startParam (в initDataUnsafe.start_param)
  const maxStartParam = window.WebApp?.initDataUnsafe?.start_param;
  if (maxStartParam) return maxStartParam;

  // 3. URL query param (fallback)
  const urlParams = new URLSearchParams(window.location.search);
  const clinicId = urlParams.get('clinic_id');
  if (clinicId) return clinicId;

  return null;
}

class ApiClient {
  private instance: AxiosInstance;
  private onUnauthorizedCallback: (() => void) | null = null;

  constructor() {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    this.instance = axios.create({
      // baseURL не используем - пути указываем полные (/webapp/...)
      baseURL: baseURL.replace(/\/api\/?$/, ''), // убираем /api из baseURL если есть
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - добавляем авторизацию
    this.instance.interceptors.request.use(
      (config) => {
        const initData = getPlatformInitData();
        config.headers = config.headers || {};

        if (initData) {
          config.headers.Authorization = `${initData.prefix} ${initData.data}`;
          // Передаем платформу отдельным заголовком для бэкенда
          config.headers['X-Platform'] = initData.prefix === 'tma' ? 'telegram' : 'max';
        } else {
          console.warn('[ApiClient] No initData available - not running in Telegram/MAX?');
        }

        // Добавляем clinicId если доступен (для новых пользователей)
        const clinicId = getClinicId();
        if (clinicId) {
          config.headers['X-Clinic-ID'] = clinicId;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - обработка ошибок
    this.instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiErrorResponse>) => {
        if (error.response?.status === 401) {
          if (this.onUnauthorizedCallback) {
            this.onUnauthorizedCallback();
          }
        }

        console.error('[ApiClient] Error:', {
          status: error.response?.status,
          message: error.response?.data?.message || error.message,
          url: error.config?.url,
        });

        return Promise.reject(error);
      }
    );
  }

  public onUnauthorized(callback: () => void): void {
    this.onUnauthorizedCallback = callback;
  }

  public get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.get<T>(url, config).then((r) => r.data);
  }

  public post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.post<T>(url, data, config).then((r) => r.data);
  }

  public put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.put<T>(url, data, config).then((r) => r.data);
  }

  public patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.patch<T>(url, data, config).then((r) => r.data);
  }

  public delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.delete<T>(url, config).then((r) => r.data);
  }

  public getAxiosInstance(): AxiosInstance {
    return this.instance;
  }
}

export const apiClient = new ApiClient();
export type { ApiErrorResponse };

